import type { LearnTopic } from "../topic-types";

const JOURNAL_CTA = {
  type: "cta" as const,
  text: "Want to privately document what you're experiencing? Your journal is visible only to you.",
  label: "Open Abuse Journal",
  to: "/abuse-journal",
};

const CHECK_CTA = {
  type: "cta" as const,
  text: "Not sure about some behaviours in your relationship? A short, private reflection tool can help you put words to what you notice.",
  label: "Take the Relationship Safety Check",
  to: "/abuse-journal?tab=check",
};

/**
 * Abusive Relationships.
 *
 * Educational, non-diagnostic module inside Understand: naming behaviours,
 * recognising patterns and understanding the impact of abuse on the nervous
 * system — always leaving conclusions with the reader.
 */
export const ABUSIVE_RELATIONSHIPS_TOPIC: LearnTopic = {
  slug: "abusive-relationships",
  emoji: "🛡️",
  title: "Abusive Relationships",
  tagline: "Understanding behaviours, patterns and their impact",
  theme: "gold",
  category: "understanding-trauma",
  isNew: true,
  cardBlurb:
    "Understand abuse, recognise patterns and learn why these relationships affect the nervous system so deeply.",
  seoDescription:
    "Trauma-informed education on abusive relationships: emotional, psychological, verbal, physical, sexual, financial and digital abuse, coercive control, gaslighting, trauma bonding, why leaving is hard, and what recovery can look like. From Recovery Trauma™.",
  intro: [
    "Abuse can be difficult to name from the inside. It often builds slowly, mixes with real affection and good memories, and can leave you doubting your own perception long before you ever use the word 'abuse'.",
    "This section explains behaviours and patterns in plain, non-judgemental language: what different forms of abuse can look like, how control is often established, why these relationships can be so hard to leave, and how they can affect the nervous system, self-esteem and sense of self.",
    "Nothing here can tell you what your relationship is. Only you can decide how to describe your own experience, and you are allowed to take your time. Read what feels useful, pause whenever you need to, and leave the rest.",
  ],
  closing:
    "Reading about abuse can bring up a lot — recognition, grief, anger, numbness, or nothing at all. All of those responses make sense. If you are frightened, or unsure whether you are safe right now, please reach out for support.",
  continueTo: [
    { label: "Abuse Journal", to: "/abuse-journal" },
    { label: "Trauma Bonds", to: "/learn/trauma-bonds" },
    { label: "Boundaries", to: "/learn/boundaries" },
    { label: "Complex PTSD", to: "/learn/cptsd" },
    { label: "Safety & Stabilisation", to: "/safety-stabilisation" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
  sections: [
    {
      slug: "what-abuse-is",
      emoji: "📚",
      title: "What abuse can look like",
      blurb: "Plain-English descriptions of the different forms abuse can take.",
      intro:
        "Abuse is about a pattern of behaviour that controls, frightens, degrades or harms another person. It can take many forms, and more than one form is often present at the same time.",
      resources: [
        {
          slug: "what-is-an-abusive-relationship",
          kind: "learn",
          title: "What is an abusive relationship?",
          summary: "A pattern of behaviour, not a single moment or a bad week.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "An abusive relationship is generally described as one where a repeated pattern of behaviour is used — knowingly or not — to gain or keep power over another person. It can involve fear, control, degradation, or harm to your body, mind, finances or freedom. Conflict, hurt feelings and mistakes happen in every relationship; abuse is different because of the pattern, the power imbalance and the effect it has on your ability to be yourself.",
            },
            {
              type: "list",
              title: "Things that are often true, and often surprising",
              items: [
                "Abuse can happen without any physical violence at all",
                "Abuse can exist alongside love, shared history, good times and genuine care",
                "Abuse can happen in any relationship — partners, family, friends, carers, colleagues",
                "Abuse can happen to anyone, regardless of gender, age, income, education or confidence",
                "Many people recognise the behaviours long before they recognise the word",
              ],
            },
            {
              type: "note",
              text: "Reading an article cannot tell you whether you are in an abusive relationship, and nothing here is a diagnosis or an instruction. It is information you can hold up against your own experience, at your own pace.",
            },
            CHECK_CTA,
          ],
        },
        {
          slug: "emotional-abuse",
          kind: "learn",
          title: "Emotional abuse",
          summary: "Behaviour that wears down how you feel about yourself.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Emotional abuse describes ongoing behaviour that undermines a person's sense of worth, safety or reality. Because it leaves no visible marks, it is often minimised — by others and by the person experiencing it — even when its effects last for years.",
            },
            {
              type: "list",
              title: "Behaviours often described as emotional abuse",
              items: [
                "Constant criticism, mockery or 'jokes' at your expense",
                "Withdrawal of affection or silent treatment as punishment",
                "Blaming you for their moods, choices or behaviour",
                "Dismissing your feelings as too sensitive, dramatic or crazy",
                "Conditional warmth — care that depends on your compliance",
                "Threatening to leave, self-harm or expose you when you disagree",
              ],
            },
            JOURNAL_CTA,
          ],
        },
        {
          slug: "psychological-and-verbal-abuse",
          kind: "learn",
          title: "Psychological and verbal abuse",
          summary: "Words, threats and mind games that create fear and self-doubt.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Psychological abuse targets your thinking, your memory and your sense of what is real. Verbal abuse is one common route into that: shouting, name-calling, humiliation, sarcasm framed as honesty, or long interrogations that leave you exhausted and unsure of yourself.",
            },
            {
              type: "list",
              title: "What people often notice",
              items: [
                "Feeling confused, foggy or unable to think clearly around this person",
                "Rehearsing conversations in advance to avoid a reaction",
                "Being told your memory of events is wrong, again and again",
                "Being shamed for things you shared in trust",
                "Feeling smaller, quieter or less like yourself over time",
              ],
            },
          ],
        },
        {
          slug: "physical-abuse",
          kind: "learn",
          title: "Physical abuse",
          summary: "Any use of physical force or restriction, including the 'small' things.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Physical abuse includes any use of force or physical restriction — hitting, pushing, grabbing, blocking a doorway, throwing objects, driving dangerously, harming pets, or withholding food, medication or sleep. Behaviour that is minimised as 'not that bad' still counts, and often escalates over time.",
            },
            {
              type: "note",
              text: "If you are in immediate danger, please contact emergency services. Our Crisis Resources page lists domestic abuse helplines and support services.",
            },
            JOURNAL_CTA,
          ],
        },
        {
          slug: "sexual-abuse-and-coercion",
          kind: "learn",
          title: "Sexual abuse and coercion",
          summary: "Consent, pressure and why 'giving in' is not the same as saying yes.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Sexual abuse within a relationship includes any sexual contact or activity that happens without free, willing and ongoing consent. Coercion can look like persistent pressure, guilt, sulking, threats, or making you feel that refusing will cost you safety, affection or peace. Agreeing because refusing feels unsafe is not consent.",
            },
            {
              type: "list",
              title: "Some things worth knowing",
              items: [
                "Consent can be withdrawn at any point, including after it was given",
                "Being in a relationship or marriage does not remove the need for consent",
                "Being pressured into acts you are uncomfortable with is a form of abuse",
                "Confusion, freezing or going along with it are common survival responses, not agreement",
              ],
            },
            {
              type: "note",
              text: "If this section is difficult, it is completely okay to close it and return another day, or not at all.",
            },
          ],
        },
        {
          slug: "financial-abuse",
          kind: "learn",
          title: "Financial abuse",
          summary: "Control over money, work and the practical means to leave.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Financial abuse restricts someone's access to money, work or resources, which can make independence feel impossible. It is one of the most common reasons people stay in, or return to, a harmful relationship.",
            },
            {
              type: "list",
              title: "Behaviours often described as financial abuse",
              items: [
                "Controlling all accounts, or requiring you to justify every purchase",
                "Preventing you from working, studying or being paid",
                "Taking your wages, benefits or savings",
                "Running up debt in your name, or coercing you into loans",
                "Giving you an 'allowance' while spending freely themselves",
                "Sabotaging your job — interruptions, arguments before work, transport problems",
              ],
            },
            JOURNAL_CTA,
          ],
        },
      ],
    },
    {
      slug: "control-and-tactics",
      emoji: "🔍",
      title: "Control and tactics",
      blurb: "Coercive control, monitoring, isolation, intimidation and stalking.",
      intro:
        "Control is rarely announced. It usually arrives as concern, love or practicality, and only becomes visible once your world has narrowed.",
      resources: [
        {
          slug: "coercive-control",
          kind: "learn",
          title: "Coercive control",
          summary: "A pattern of rules, monitoring and consequences that shrinks your freedom.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Coercive control describes an ongoing pattern of behaviour designed to make another person dependent, compliant and frightened of the consequences of stepping out of line. Individually the incidents can look small; together they form a system of rules you live inside. In England and Wales, coercive or controlling behaviour in an intimate or family relationship is a criminal offence.",
            },
            {
              type: "list",
              title: "What it can look like",
              items: [
                "Rules about what you wear, eat, spend, watch or say",
                "Needing permission, or facing punishment for ordinary choices",
                "Being checked up on, timed, or asked to account for every hour",
                "Threats about children, pets, immigration status, money or reputation",
                "Consequences that are never openly stated but always understood",
              ],
            },
            CHECK_CTA,
          ],
        },
        {
          slug: "digital-abuse-and-monitoring",
          kind: "learn",
          title: "Digital abuse and monitoring",
          summary: "Phones, location sharing, accounts and constant contact.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Digital abuse uses technology to monitor, pressure or humiliate. It is often framed as closeness — 'we share everything' — while functioning as surveillance.",
            },
            {
              type: "list",
              items: [
                "Demanding passwords, or reading your messages and emails",
                "Location tracking, or requiring photo proof of where you are",
                "Expecting instant replies, with anger if you don't respond",
                "Posting, threatening to post, or sharing private images",
                "Controlling or impersonating you on your own accounts",
                "Installing apps or devices that monitor you",
              ],
            },
            {
              type: "note",
              text: "If you think a device may be monitored, consider using a device the other person cannot access when looking for support. Our Safety & Privacy panel in the Abuse Journal has more on this.",
            },
          ],
        },
        {
          slug: "stalking",
          kind: "learn",
          title: "Stalking",
          summary: "Persistent, unwanted contact or attention that causes fear.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Stalking is a pattern of unwanted, repeated attention that causes alarm or distress. It can occur during a relationship and often escalates after it ends. It can include turning up uninvited, repeated calls or messages, contacting your friends, family or employer, sending gifts, or watching and following you.",
            },
            {
              type: "note",
              text: "Keeping a dated record of incidents can help if you ever choose to seek support from police or a specialist service. Your Abuse Journal is designed for exactly that — and stays private to you.",
            },
            JOURNAL_CTA,
          ],
        },
        {
          slug: "isolation",
          kind: "learn",
          title: "Isolation from friends and family",
          summary: "How a world quietly narrows to one person.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Isolation is often gradual and rarely feels like isolation while it happens. It can begin as jealousy, criticism of the people who care about you, or conflict that reliably occurs before you see them — until seeing them costs more than staying home.",
            },
            {
              type: "list",
              items: [
                "Criticism or suspicion of your friends and family",
                "Sulking, arguments or 'emergencies' whenever you make plans",
                "Being moved away from your support network",
                "Being told nobody else understands you or would put up with you",
                "Slowly declining invitations to keep the peace",
              ],
            },
          ],
        },
        {
          slug: "intimidation-and-threats",
          kind: "learn",
          title: "Intimidation and threats",
          summary: "Fear created without a hand ever being raised.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Intimidation creates fear through presence, implication and unpredictability: punching walls, breaking your belongings, standing over you, driving fast, displaying weapons, or threats about what would happen if you left. Once fear is established, the threat rarely needs repeating — a look or a tone of voice can be enough.",
            },
          ],
        },
      ],
    },
    {
      slug: "manipulation-and-cycles",
      emoji: "🔄",
      title: "Manipulation and cycles",
      blurb: "Gaslighting, love bombing, trauma bonding and the cycle of abuse.",
      resources: [
        {
          slug: "gaslighting",
          kind: "learn",
          title: "Gaslighting",
          summary: "When your own perception starts to feel unreliable.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Gaslighting describes behaviour that leads someone to doubt their own memory, perception or sanity. It can be deliberate or simply a habitual defence, and its effect is the same: you begin to trust their version of events more than your own.",
            },
            {
              type: "list",
              title: "Phrases people often recognise",
              items: [
                "'That never happened — you're imagining things'",
                "'You're too sensitive / you always overreact'",
                "'You're remembering it wrong'",
                "'Everyone agrees with me about you'",
                "'I never said that, you're making things up again'",
              ],
            },
            {
              type: "note",
              text: "Writing down what happened soon after it happens — dates, words, how you felt — can help you hold on to your own account when it is being questioned.",
            },
            JOURNAL_CTA,
          ],
        },
        {
          slug: "manipulation-and-love-bombing",
          kind: "learn",
          title: "Manipulation and love bombing",
          summary: "Intensity, guilt and obligation used to steer your choices.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Manipulation influences your behaviour through guilt, obligation, flattery, pity or fear rather than honest conversation. Love bombing is one intense form of it: overwhelming affection, attention, gifts or future-planning very early on, or after harm, which can create a sense of debt and make later mistreatment harder to name.",
            },
            {
              type: "list",
              items: [
                "Extremely fast intensity — 'soulmates', moving in, commitment within weeks",
                "Grand gestures that arrive right after an argument or betrayal",
                "Guilt when you set a limit: 'after everything I've done for you'",
                "Pity that turns your concern into responsibility for their behaviour",
              ],
            },
          ],
        },
        {
          slug: "trauma-bonding-and-intermittent-reinforcement",
          kind: "learn",
          title: "Trauma bonding and intermittent reinforcement",
          summary: "Why unpredictable kindness can create the strongest attachments.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "When harm and comfort come from the same person, the nervous system can form an attachment that feels far stronger than an ordinary bond. Kindness that arrives unpredictably — especially after fear or distance — is powerfully reinforcing, which is why on-off relationships can feel more gripping than steady ones.",
            },
            {
              type: "flow",
              title: "A pattern many people recognise",
              items: [
                "Tension builds",
                "Something painful happens",
                "Distance, silence or fear",
                "Warmth, apology or relief",
                "Hope returns, and the bond deepens",
              ],
            },
            {
              type: "prose",
              text: "This is a nervous-system pattern, not a sign of weak judgement or a wish to be treated badly. Understanding it often reduces the shame that builds up around staying.",
            },
            {
              type: "cta",
              text: "Go deeper into how these bonds form and gently unwind.",
              label: "Open the Trauma Bonds module",
              to: "/learn/trauma-bonds",
            },
          ],
        },
        {
          slug: "the-cycle-of-abuse",
          kind: "learn",
          title: "The cycle of abuse",
          summary: "Tension, incident, reconciliation, calm — and why the calm hurts too.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Many people describe abuse as moving in cycles: tension building, an incident, then reconciliation — apologies, promises, affection — followed by a calm period sometimes called the honeymoon phase. The calm can be genuinely lovely, which is part of what makes the pattern so disorienting.",
            },
            {
              type: "flow",
              items: ["Tension building", "Incident", "Reconciliation", "Calm", "Tension building again"],
            },
            {
              type: "note",
              text: "Not every relationship follows a neat cycle. Some are unpredictable with no calm phase at all, and that does not make the experience less real.",
            },
          ],
        },
        {
          slug: "walking-on-eggshells",
          kind: "reflect",
          title: "Walking on eggshells",
          summary: "The exhausting work of managing someone else's reactions.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "'Walking on eggshells' describes living in constant anticipation of someone else's mood — scanning, softening, editing yourself, keeping the peace. It is skilled, exhausting work, and it usually goes unnoticed because it is invisible from the outside.",
            },
            {
              type: "prompts",
              title: "Gentle reflection",
              items: [
                "What do I change about myself before this person comes home?",
                "What topics have I quietly stopped raising?",
                "How does my body feel when I hear their footsteps, car or notification?",
                "Who am I when I'm away from them for a few days?",
              ],
            },
            JOURNAL_CTA,
          ],
        },
      ],
    },
    {
      slug: "impact",
      emoji: "🌊",
      title: "Impact and why leaving is hard",
      blurb: "The nervous system, self-esteem, attachment, and the reality of leaving.",
      resources: [
        {
          slug: "why-leaving-is-difficult",
          kind: "learn",
          title: "Why leaving can be difficult",
          summary: "Practical, emotional and physiological reasons that all coexist.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "'Why don't you just leave?' is one of the least useful questions ever asked of someone in a harmful relationship. Leaving is rarely a single decision; it is a process, and it is often the most dangerous moment in the relationship.",
            },
            {
              type: "list",
              title: "Reasons people stay, all of which make sense",
              items: [
                "Fear of what they will do — to you, children, pets or themselves",
                "No money, no housing, no independent income",
                "Immigration status, shared debt, or a shared business",
                "Love, hope, and the memory of who they were",
                "Isolation — nobody left to turn to",
                "Shame, or not being believed before",
                "Being told, and half-believing, that it is your fault",
                "A nervous system shaped to survive the relationship rather than exit it",
              ],
            },
            {
              type: "note",
              text: "Statistically, the period around leaving carries elevated risk. Specialist domestic abuse services can help with safety planning, whether or not you have decided to leave.",
            },
          ],
        },
        {
          slug: "why-people-return",
          kind: "learn",
          title: "Why people sometimes return",
          summary: "Returning is common — and it is not failure.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Many people leave and return several times before leaving for good, and that is a well-documented pattern rather than a personal failing. Grief, hope, financial pressure, children, loneliness, pressure from others and the pull of a trauma bond can all draw someone back.",
            },
            {
              type: "prose",
              text: "If you have returned before, you have not used up your chances. Each time tends to add information, not remove options.",
            },
          ],
        },
        {
          slug: "survival-responses",
          kind: "learn",
          title: "Fear, freeze, fawn and survival responses",
          summary: "Why you may not have fought back, or left, or even felt afraid.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Under threat, the body chooses automatically: fight, flight, freeze or fawn. Freezing, going blank, being unable to speak, or becoming extremely accommodating are not choices or consent — they are protective responses that happen faster than thought.",
            },
            {
              type: "list",
              items: [
                "Fight — arguing back, anger, defending yourself",
                "Flight — leaving the room, staying busy, avoiding home",
                "Freeze — going still, blank, numb or unable to speak",
                "Fawn — appeasing, apologising, anticipating needs to prevent harm",
              ],
            },
            {
              type: "note",
              text: "Many people feel shame about fawning in particular. It is one of the most effective survival strategies there is, and it kept you safer.",
            },
          ],
        },
        {
          slug: "abuse-and-the-nervous-system",
          kind: "learn",
          title: "Abuse and the nervous system",
          summary: "Living in survival mode, and what that costs the body.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When threat is ongoing and unpredictable, the nervous system stays braced. That can show up as hypervigilance, poor sleep, digestive problems, startle responses, exhaustion, numbness, or difficulty concentrating — the body doing exactly what it should under sustained stress.",
            },
            {
              type: "prose",
              text: "These responses can continue for a long time after the relationship ends. That is not a sign that you are broken or that you have failed to move on; a body that learned to expect danger needs repeated evidence of safety before it settles.",
            },
            {
              type: "cta",
              text: "Understanding your own capacity for calm can help make sense of these responses.",
              label: "Open Window of Tolerance",
              to: "/learn/window-of-tolerance",
            },
          ],
        },
        {
          slug: "self-esteem-attachment-cptsd",
          kind: "learn",
          title: "Self-esteem, attachment and Complex PTSD",
          summary: "How prolonged harm shapes self-image and closeness.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Repeated criticism, blame and control tend to become internalised. Many people leave a relationship carrying an inner voice that sounds remarkably like the person who harmed them — and mistake it for the truth about themselves.",
            },
            {
              type: "list",
              title: "What people often notice afterwards",
              items: [
                "Difficulty trusting your own judgement or memory",
                "Feeling fundamentally unlovable, too much, or not enough",
                "Anxiety when someone is kind, or when closeness increases",
                "Pulling away, or holding on tightly, sometimes both",
                "Emotional flashbacks — sudden floods of old feeling with no clear trigger",
              ],
            },
            {
              type: "prose",
              text: "When harm is prolonged and escape feels impossible, the effects are sometimes described as Complex PTSD. It is a description of what happened to you, not a statement about who you are.",
            },
            {
              type: "cta",
              text: "Explore how complex trauma develops and what supports recovery.",
              label: "Open Complex PTSD",
              to: "/learn/cptsd",
            },
          ],
        },
      ],
    },
    {
      slug: "recognising",
      emoji: "🚩",
      title: "Recognising patterns",
      blurb: "Red flags, and the difference between unhealthy and abusive.",
      resources: [
        {
          slug: "red-flags",
          kind: "learn",
          title: "Recognising red flags",
          summary: "Early signals that are easy to explain away.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Red flags are not proof of anything on their own. They are signals worth noticing — especially when several appear together, or when they repeat after you have raised them.",
            },
            {
              type: "list",
              items: [
                "Intensity very early on, and pressure to commit quickly",
                "Jealousy framed as love, or monitoring framed as care",
                "Discomfort with your friendships, family or independence",
                "Difficulty accepting a 'no' of any size",
                "Blame that always lands somewhere other than themselves",
                "Different behaviour in private than in public",
                "Feeling on edge, apologetic or smaller around them",
              ],
            },
            CHECK_CTA,
          ],
        },
        {
          slug: "healthy-unhealthy-abusive",
          kind: "learn",
          title: "Healthy, unhealthy and abusive behaviours",
          summary: "Three descriptions, and why the difference matters.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Not every painful relationship is abusive, and not every abusive relationship looks dramatic. It can help to think in three broad descriptions rather than a single yes-or-no verdict.",
            },
            {
              type: "steps",
              items: [
                {
                  label: "Healthy",
                  note: "Disagreement without fear. Limits are respected. Repair happens. You can be yourself, keep your relationships and change your mind.",
                },
                {
                  label: "Unhealthy",
                  note: "Poor communication, jealousy, defensiveness or unfairness — often changeable when both people are willing and safe to work on it.",
                },
                {
                  label: "Abusive",
                  note: "A pattern of control, fear or harm where one person's power over the other is maintained. Raising it tends to make things worse rather than better.",
                },
              ],
            },
            {
              type: "note",
              text: "These descriptions are here to support your own reflection. Only you get to decide how to name your experience, and you may not want to name it at all yet.",
            },
          ],
        },
      ],
    },
    {
      slug: "recovery",
      emoji: "🌱",
      title: "Recovery and support",
      blurb: "Rebuilding boundaries, self-trust, safety and support.",
      resources: [
        {
          slug: "what-recovery-can-look-like",
          kind: "learn",
          title: "What recovery after abuse can look like",
          summary: "Non-linear, unglamorous, and entirely yours to pace.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Recovery after abuse is rarely a clean arc. It often includes grief for the relationship you hoped for, anger that arrives late, numbness, relief and guilt for feeling relieved — sometimes all in one afternoon.",
            },
            {
              type: "list",
              title: "Signs of movement people often overlook",
              items: [
                "Noticing a behaviour and naming it, even quietly to yourself",
                "Sleeping better, or eating without a knot in your stomach",
                "Making a small decision without rehearsing how to justify it",
                "Feeling anger where you used to feel only fault",
                "Reconnecting with one person you had lost touch with",
              ],
            },
          ],
        },
        {
          slug: "rebuilding-boundaries",
          kind: "practice",
          title: "Rebuilding boundaries",
          summary: "Starting small, where the stakes are low.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "If boundaries were punished, setting them can feel dangerous long after the danger has passed. Starting with low-stakes limits — with safe people, in small moments — gives your nervous system evidence that a 'no' can be survivable.",
            },
            {
              type: "steps",
              items: [
                { label: "Notice", note: "Where in your body do you feel the 'I don't want this'?" },
                { label: "Name it privately", note: "You don't have to say it out loud to count it." },
                { label: "Try one small limit", note: "A delayed reply, a declined invitation, a preference stated." },
                { label: "Expect the aftershock", note: "Guilt and anxiety often follow. They pass, and they get quieter." },
              ],
            },
            {
              type: "cta",
              text: "There is a full module on rebuilding boundaries gently.",
              label: "Open Boundaries",
              to: "/learn/boundaries",
            },
          ],
        },
        {
          slug: "rebuilding-trust-in-yourself",
          kind: "reflect",
          title: "Rebuilding trust in yourself",
          summary: "Recovering the perception that was called unreliable.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When your version of events has been questioned for a long time, self-trust erodes. Rebuilding it is usually less about big insights and more about small repeated evidence that your perception is worth listening to.",
            },
            {
              type: "prompts",
              title: "Prompts you can take into your journal",
              items: [
                "What did I notice at the time that I later talked myself out of?",
                "What does my body tell me before my mind produces an explanation?",
                "When have I been right about something and doubted it anyway?",
                "What would I say to a friend describing exactly what I've described?",
              ],
            },
            JOURNAL_CTA,
          ],
        },
        {
          slug: "safety-and-seeking-support",
          kind: "learn",
          title: "Safety and seeking support",
          summary: "Options that exist whether or not you are ready to leave.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "You do not have to be certain, ready or 'bad enough' to reach out. Specialist domestic abuse services support people who are still in relationships, unsure about them, or long out of them — including with safety planning, housing, legal information and emotional support.",
            },
            {
              type: "list",
              title: "Practical things some people find helpful",
              items: [
                "Keeping a dated private record of incidents",
                "Using a device the other person cannot access when seeking help",
                "Telling one trusted person what is happening",
                "Keeping copies of important documents somewhere safe",
                "Having a plan for where you would go, even if you never use it",
              ],
            },
            {
              type: "cta",
              text: "Helplines, domestic abuse services and immediate support.",
              label: "Open Crisis Resources",
              to: "/crisis",
            },
            JOURNAL_CTA,
          ],
        },
      ],
    },
  ],
};
